import { useLocation, useNavigate, useParams } from '@tanstack/react-router'
import { useRuntimeVariables } from 'app-kit'
import { URL_PATH_DRILLDOWN_EXPLORATION_VIEWER } from 'features/drilldown'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'

import { BottomSection } from './BottomSection'
import { HeaderSection } from './HeaderSection'
import { MidSection } from './MidSection'
import { useNavbarData } from './useNavbarData'

export const SidebarWidth = '220px'

const StyledSidebar = styled.div<{ $width: string }>`
  display: flex;
  flex-direction: column;
  height: 100vh; /* Use the full height of the viewport */
  width: ${(props) => props.$width};
  min-width: ${(props) => props.$width};
  background-color: ${({ theme }) => theme.forecast.navSidebarBackground};
  padding: 8px;
  overflow-y: auto; /* Allow vertical scrolling */
  overflow-x: hidden; /* Hide horizontal scrollbar */
  position: sticky; /* Keep it in place relative to the viewport */
`

const getSelectedTab = (pathname: string) => {
  if (pathname.includes('/assets')) return 'Assets' as const
  if (pathname.includes('/inspections')) return 'Inspections' as const
  if (pathname.includes('publisher')) return 'Publisher' as const
  if (pathname.includes('/health-plans')) return 'Health Plans' as const
  if (pathname.includes('/scheduling')) return 'Inspection Scheduling' as const
  if (pathname.includes(`/overview`)) return 'Overview' as const
  if (pathname.includes(URL_PATH_DRILLDOWN_EXPLORATION_VIEWER))
    return 'Exploration' as const
  if (pathname.includes('/explorer')) return 'Explorer App' as const
  if (pathname.includes('/parts')) return 'Parts Thread' as const
  if (pathname.includes('/jobs')) return 'Jobs Overview' as const
  if (pathname.includes('/walkdowns')) return 'Walkdowns' as const
  if (pathname.includes('/inventory')) return 'Inventory Management' as const
  if (pathname.includes('/maximo')) return 'Maximo' as const
  if (pathname.includes('/process-flows'))
    return 'Process Flow Diagrams' as const
}

export type SelectedTab = ReturnType<typeof getSelectedTab>

export const Navbar: React.FC<{
  siteID?: string | undefined
  defaultClosed?: boolean
  hideSiteSelector?: boolean
}> = ({ siteID, defaultClosed = false, hideSiteSelector = false }) => {
  const navigate = useNavigate()
  const [isLeftSidebarOpen, setIsLeftSidebarOpen] = useState(!defaultClosed)
  const { BASE_PATH } = useRuntimeVariables()
  const toggleIsLeftSidebarOpen = useCallback(() => {
    setIsLeftSidebarOpen((prev) => !prev)
  }, [])
  const sidebarWidth = isLeftSidebarOpen ? '220px' : '56px'
  const location = useLocation()
  const { siteID: urlSiteID } = useParams({ strict: false })
  const { site, organization } = useNavbarData({
    siteID: urlSiteID ?? siteID,
  })

  const isNoSitesRoute = location.pathname.startsWith('/no-sites/')
  const shouldHideSiteSelector = hideSiteSelector || isNoSitesRoute

  useEffect(() => {
    //if screensize is less than 600px, close the sidebar
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setIsLeftSidebarOpen(false)
      }
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  React.useEffect(() => {
    // If Forecast picked a default site, and it's not in the URL yet, redirect
    if (
      site &&
      !siteID &&
      (location.pathname === `/${BASE_PATH}/assets` ||
        location.pathname === `/${BASE_PATH}/assets/` ||
        location.pathname === `/forecast/assets` ||
        location.pathname === `/forecast/assets/` ||
        location.pathname === '/assets' ||
        location.pathname === '/assets/')
    ) {
      navigate({ to: `/assets/${site.id}` })
    }
  }, [BASE_PATH, location.pathname, navigate, site, siteID])
  // Determine which siteID to use: param siteID if available, otherwise fallback to site.id
  const resolvedSiteID = siteID ?? site?.id
  const selectedTab = getSelectedTab(location.pathname + location.search)

  return (
    <StyledSidebar $width={sidebarWidth}>
      <HeaderSection
        isLeftSidebarOpen={isLeftSidebarOpen}
        siteID={resolvedSiteID}
        hideSiteSelector={shouldHideSiteSelector}
      />
      {!resolvedSiteID ? null : (
        <MidSection
          isLeftSidebarOpen={isLeftSidebarOpen}
          siteID={resolvedSiteID}
          selectedTab={selectedTab}
        />
      )}
      <BottomSection
        isLeftSidebarOpen={isLeftSidebarOpen}
        toggleIsLeftSidebarOpen={toggleIsLeftSidebarOpen}
        orgID={organization?.id}
      />
    </StyledSidebar>
  )
}
